import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridToolbar
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  TextField
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

export const MarketsManagement: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all markets
  const { data: marketsResponse, isLoading: loadingMarkets } = useQuery({
    queryKey: ['admin', 'markets'],
    queryFn: async () => {
      return api.get('/api/marketcompass/admin/markets/all');
    }
  });

  const marketsData = marketsResponse?.data?.markets || [];

  // Fetch all reports to count per market
  const { data: reportsData } = useQuery({
    queryKey: ['admin', 'all-reports'],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/reports/all');
      return response.data.reports;
    }
  });

  // Fetch all podcasts to count per market
  const { data: podcastsData } = useQuery({
    queryKey: ['admin', 'all-podcasts'],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/podcasts/all');
      return response.data.podcasts;
    }
  });

  // Combine data to include counts
  const marketsWithCounts = React.useMemo(() => {
    if (!marketsData || !Array.isArray(marketsData)) return [];
    
    return marketsData.map((market: any) => {
      const reportCount = reportsData?.filter((r: any) => r.market_id === market.id).length || 0;
      const podcastCount = podcastsData?.filter((p: any) => p.market_id === market.id).length || 0;
      const latestReport = reportsData?.filter((r: any) => r.market_id === market.id)
        .sort((a: any, b: any) => b.report_month - a.report_month)[0];
      
      return {
        ...market,
        report_count: reportCount,
        podcast_count: podcastCount,
        latest_report_month: latestReport?.report_month || null
      };
    });
  }, [marketsData, reportsData, podcastsData]);

  // Helper function to format the latest report period
  const formatLatestReportPeriod = (reportMonth: number | null) => {
    if (!reportMonth) {
      // If no reports, use current date minus 50 days
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() - 50);
      return fallbackDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    
    const year = Math.floor(reportMonth / 100);
    const month = reportMonth % 100;
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const columns: GridColDef[] = [
    {
      field: 'continent_name',
      headerName: 'Continent',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      )
    },
    {
      field: 'country_name',
      headerName: 'Country',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      )
    },
    {
      field: 'name',
      headerName: 'Market',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Is Active',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        params.value ? (
          <CheckIcon color="success" />
        ) : (
          <CloseIcon color="error" />
        )
      )
    },
    {
      field: 'latest_report_month',
      headerName: 'Latest Report Period',
      width: 200,
      renderCell: (params) => formatLatestReportPeriod(params.value)
    },
    {
      field: 'report_count',
      headerName: '# Reports',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" textAlign="center">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'podcast_count',
      headerName: '# Podcasts',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" textAlign="center">
          {params.value || 0}
        </Typography>
      )
    }
  ];

  const isLoading = loadingMarkets || !reportsData || !podcastsData;

  // Filter markets based on search term
  const filteredMarkets = React.useMemo(() => {
    if (!searchTerm) return marketsWithCounts;
    
    return marketsWithCounts.filter((market) => 
      market.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [marketsWithCounts, searchTerm]);

  return (
    <Box sx={{ height: 650, width: '100%' }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by Market Name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      <DataGrid
        rows={filteredMarkets}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        components={{
          Toolbar: GridToolbar
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 }
          },
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }]
          }
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          navigate(`/admin/market/${params.row.id}`);
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer'
          }
        }}
      />
    </Box>
  );
};